import { css } from '@inter-ikea-kompis/web-component';
import type { ITheme } from '@inter-ikea-kompis/types';

import { Text } from '@ingka/common-styles-webc';

import ProductCardLayoutEnum from '../../enums/ProductCardLayoutEnum.js';
import ProductCardOrderEnum from '../../enums/ProductCardOrderEnum.js';
import ProductCardSelectBehaviourEnum from '../../enums/ProductCardSelectBehaviourEnum.js';
import ProductCardSelectedStateEnum from '../../enums/ProductCardSelectedStateEnum.js';

const FUNCTION_ICON_SIZE = 40;
const CHECKBOX_ICON_SIZE = 32;
const CONTAINER_MAX_WIDTH = 600;

/**
 * A class with style.
 */
export default class ListGalleryCardStyle {
	/**
	 * Returns styles.
	 *
	 * @param options Options object.
	 * @param options.theme Theme.
	 * @param options.layout ...
	 * @param options.selectedState ...
	 * @param options.hasFocus ...
	 * @param options.hasZipAvailabilities ...
	 * @param options.selectBehaviour ...
	 * @param options.hasProductActionElement ...
	 * @returns Styles.
	 */
	public static getStyle(options: {
		theme: ITheme;
		layout: ProductCardLayoutEnum;
		selectedState: ProductCardSelectedStateEnum;
		hasFocus: boolean;
		hasZipAvailabilities: boolean;
		selectBehaviour: ProductCardSelectBehaviourEnum;
		hasProductActionElement: boolean;
	}): string {
		const { spacing, color, elevation } = options.theme;

		return css`
			${Text.CSS}

			:host {
				display: block;
			}

			.container {
				position: relative;
				outline: none;
				padding: ${spacing.space100 / 16}rem;
				max-width: ${CONTAINER_MAX_WIDTH / 16}rem;
			}

			.innerContainer {
				text-decoration: none;
				color: ${color.text.dark};
				cursor: ${options.hasProductActionElement ? 'pointer' : 'auto'};
				display: flex;
				flex-direction: column;
				outline: none;
			}

			.checkboxIconContainer .checkboxIcon {
				display: flex;
				justify-content: center;
				align-items: center;
				width: ${CHECKBOX_ICON_SIZE / 16}rem;
				height: ${CHECKBOX_ICON_SIZE / 16}rem;
				background-color: ${color.background.inverse};
				color: ${color.text.inverse};
				margin: ${spacing.space25 / 16}rem;
				border-radius: 50%;
				box-shadow: ${elevation.size.default} ${elevation.color.default};
			}

			kompis-product-card-media {
				order: ${ProductCardOrderEnum.beforeFirstFocusElement};
			}

			.priceModuleWrapper {
				margin-top: ${spacing.space50 / 16}rem;
				order: ${ProductCardOrderEnum.firstFocusElement};
				z-index: 1;
				display: flex;
			}

			.functionIcons,
			.checkboxIconContainer {
				order: ${ProductCardOrderEnum.afterFirstFocusElement};
				display: flex;
				z-index: 1;
				position: absolute;
				top: ${spacing.space100 / 16}rem;
				left: ${spacing.space100 / 16}rem;
				right: ${spacing.space100 / 16}rem;
			}

			.checkboxIconContainer {
				justify-content: flex-end;
				pointer-events: none;
			}

			.availabilityContent {
				order: ${ProductCardOrderEnum.afterFirstFocusElement};
			}

			.zipAvailabilityStatusIndicator {
				margin-top: ${spacing.space50 / 16}rem;
			}

			.storeAvailabilityStatusIndicator {
				margin-top: ${(options.hasZipAvailabilities ? 0 : spacing.space50) / 16}rem;
			}

			.productVariants {
				order: ${ProductCardOrderEnum.afterFirstFocusElement};
				margin-top: ${spacing.space75 / 16}rem;
			}

			.priceModuleWrapper kompis-price-module {
				flex-shrink: 1;
				flex-grow: 1;
				min-width: 0;
			}

			.priceModuleWrapper .separator {
				width: ${spacing.space50 / 16}rem;
			}

			.priceModuleWrapper .infoIconButton {
				flex-shrink: 0;
				flex-grow: 0;
			}

			.infoIconButton skapa-icon-button {
				position: relative;
			}

			.productVariants h4 {
				margin: 0;
				color: ${color.text.dark};
				font-weight: normal;
			}

			kompis-function-icon {
				display: block;
				width: ${FUNCTION_ICON_SIZE / 16}rem;
				margin: ${spacing.space25 / 16}rem;
			}

			skapa-status {
				min-height: ${22 / 16}rem;
			}

			.visuallyHidden:not(:focus):not(:active) {
				clip: rect(0 0 0 0);
				clip-path: inset(50%);
				height: ${1 / 16}rem;
				overflow: hidden;
				position: absolute;
				white-space: nowrap;
				width: ${1 / 16}rem;
			}

			${this.getOutlineStyle(options)}
		`;
	}

	/**
	 * Returns the outline style when focusing.
	 *
	 * @param options Options.
	 * @param options.theme Theme.
	 * @param options.hasFocus Has focus.
	 * @param options.selectedState Selected state.
	 * @param options.layout Layout.
	 * @param options.selectBehaviour Select behaviour.
	 * @returns Styles.
	 */
	private static getOutlineStyle(options: {
		theme: ITheme;
		hasFocus: boolean;
		selectedState: ProductCardSelectedStateEnum;
		selectBehaviour: ProductCardSelectBehaviourEnum;
		layout: ProductCardLayoutEnum;
	}): string {
		const { focusOutline, motion, border } = options.theme;
		const isVisible =
			options.hasFocus ||
			(options.selectedState === ProductCardSelectedStateEnum.outline &&
				options.selectBehaviour === ProductCardSelectBehaviourEnum.selectable &&
				options.layout === ProductCardLayoutEnum.list);
		return css`
			.outline {
				z-index: inherit;
				pointer-events: none;
				transition-property: top, left, right, bottom, opacity;
				transition-duration: ${motion.duration.large};
				border-radius: ${border.radius.radius4 / 16}rem;
				position: absolute;
				box-shadow: 0 0 0 ${focusOutline.width / 16}rem ${focusOutline.color};
				left: -${focusOutline.offset / 16}rem;
				top: -${focusOutline.offset / 16}rem;
				right: -${focusOutline.offset / 16}rem;
				bottom: -${focusOutline.offset / 16}rem;
				opacity: ${isVisible ? 1 : 0};
			}
		`;
	}
}
